import { addDays, addWeeks, addMonths, isWeekend } from "date-fns"
import { RecurrencePeriod } from "@/types/recurrence"
import { AttendanceEvent } from "@/entities/attendanceEvent/AttendanceEventType"
import { AttendanceStatus } from "@/types/attendanceStatus"
import { MutationOptions } from "@/hooks/useManagedMutation"
import { DocumentData } from "@/types/firestoreDocument"
import { dexie } from "@/firestore/localDatabase"
import { collectionName } from "@/entities/attendanceEvent/attendanceEventMeta"

export async function createOneOrRecurringAttendanceEvents({
   is_recurring,
   recurring_period,
   recurring_end_date,
   recurring_group,
   from,
   to,
   delayMinutes = 0,
   type,
   justified,
   young_id,
   school_year_id,
   justification_validation_status,
   justification_doc,
   admin_comment,
   expected,
   reason,
   createAttendanceEvent,
}: {
   is_recurring: boolean
   recurring_period: RecurrencePeriod
   recurring_end_date: Date
   recurring_group: string
   from: Date
   to: Date
   type: AttendanceStatus
   delayMinutes?: number
   young_id: string
   school_year_id: string
   justification_validation_status: "validated" | "upload_pending"
   justified: boolean
   justification_doc?: string
   admin_comment?: string
   expected: boolean
   reason?: string
   createAttendanceEvent: (
      doc: DocumentData<AttendanceEvent>,
      options?: MutationOptions,
   ) => Promise<string>
	}): Promise<string[]> {
	
	if (!is_recurring) {
		const attendanceEventId = await createAttendanceEvent(
			{
				school_year_id,
				young_id,
				type,
				justified,
				from,
				to,
				delayMinutes,
				justification_doc: justification_doc || "",
				justification_validation_status,
				admin_comment: admin_comment || "",
				expected,
				reason: reason || "",
				is_recurring: false
			},
			{
				quickMode: true,
			},
		)
		return [attendanceEventId]
	}

	// Recurring section

   const ids: string[] = []
   let cursorFrom = from
   let cursorTo = to

   while (cursorFrom <= recurring_end_date) {
      if (!isWeekend(cursorFrom)) {
         const attendanceEventId = await createAttendanceEvent(
            {
               school_year_id,
               young_id,
               type,
               justified,
               from: cursorFrom,
               to: cursorTo,
               delayMinutes,
               justification_doc: justification_doc || "",
               justification_validation_status,
               admin_comment: admin_comment || "",
               expected,
               reason: reason || "",
               is_recurring,
               recurring_period,
               recurring_end_date,
               recurring_group,
            },
            { quickMode: true },
         )

         ids.push(attendanceEventId)
      }

      if (recurring_period === "everyday") {
         cursorFrom = addDays(cursorFrom, 1)
         cursorTo = addDays(cursorTo, 1)
      } else if (recurring_period === "everyweek") {
         cursorFrom = addWeeks(cursorFrom, 1)
         cursorTo = addWeeks(cursorTo, 1)
      } else if (recurring_period === "everymonth") {
         cursorFrom = addMonths(cursorFrom, 1)
         cursorTo = addMonths(cursorTo, 1)
      } else {
         throw new Error("Invalid recurrence period")
      }
   }

   return ids
}

/**
 * Delete the event and all recurring events that are after the given event
 * @param param0
 */
export async function deleteRecurringAttendanceEvents({
	recurring_group,
	minEventFrom,
   deleteAttendanceEvent
}: {
	recurring_group: string
	minEventFrom: Date
   deleteAttendanceEvent: (id: string) => Promise<void>
}) {
   
	const recurringEvents = await dexie
		.table<AttendanceEvent>(collectionName)
		.where("recurring_group").equals(recurring_group)
		.and(event => event.from >= minEventFrom)
		.toArray()
	
   await Promise.all(recurringEvents.map(event => deleteAttendanceEvent(event.id)))
}

